import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  useColorScheme,
  TextInput,
  StyleSheet,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const SupportGroupScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('我的小组');
  const [searchQuery, setSearchQuery] = useState('');

  // 模拟数据 - 我的小组
  const myGroups = [
    {
      id: '1',
      name: '低碳饮食践行者',
      members: 128,
      description: '致力于分享低碳饮食经验，互相鼓励坚持健康生活方式。',
      image: 'https://via.placeholder.com/80',
      lastActivity: '今天',
      unreadMessages: 3,
    },
    {
      id: '2',
      name: '运动减重小分队',
      members: 96,
      description: '一起科学运动，健康减重。每周分享进度，互相鼓励。',
      image: 'https://via.placeholder.com/80',
      lastActivity: '昨天',
      unreadMessages: 0,
    },
  ];

  // 模拟数据 - 推荐小组
  const recommendedGroups = [
    {
      id: '3',
      name: '全麦面包爱好者',
      members: 215,
      description: '交流全麦面包的制作心得，分享健康食谱。',
      image: 'https://via.placeholder.com/80',
      tags: ['烘焙', '全麦', '低糖'],
    },
    {
      id: '4',
      name: '晨间锻炼团',
      members: 342,
      description: '每天早起锻炼，互相监督，分享晨练心得。',
      image: 'https://via.placeholder.com/80',
      tags: ['早起', '锻炼', '习惯养成'],
    },
    {
      id: '5',
      name: '素食营养均衡',
      members: 189,
      description: '素食爱好者交流如何平衡营养，分享美味素食菜谱。',
      image: 'https://via.placeholder.com/80',
      tags: ['素食', '营养均衡', '食谱'],
    },
  ];

  // 模拟数据 - 今日讨论
  const todayDiscussions = [
    {
      id: '1',
      groupId: '1',
      groupName: '低碳饮食践行者',
      title: '如何在社交场合坚持低碳饮食？',
      replies: 28,
      lastReplyTime: '1小时前',
    },
    {
      id: '2',
      groupId: '2',
      groupName: '运动减重小分队',
      title: '分享：连续坚持HIIT训练一个月的变化',
      replies: 45,
      lastReplyTime: '3小时前',
    },
  ];

  // 渲染我的小组项
  const renderMyGroupItem = ({ item }) => (
    <TouchableOpacity 
      style={[
        styles.groupCard, 
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}
      onPress={() => navigation.navigate('GroupDetail', { groupId: item.id })}
    >
      <View style={styles.groupHeader}>
        <Image source={{ uri: item.image }} style={styles.groupImage} />
        <View style={styles.groupInfo}>
          <Text style={[styles.groupName, isDarkMode && styles.textLight]}>
            {item.name}
          </Text>
          <Text style={[styles.groupMeta, isDarkMode && styles.textLight]}>
            {item.members} 成员 · 最近活动: {item.lastActivity}
          </Text>
        </View>
        {item.unreadMessages > 0 && (
          <View style={styles.badgeContainer}>
            <Text style={styles.badgeText}>{item.unreadMessages}</Text>
          </View>
        )}
      </View>
      <Text style={[styles.groupDescription, isDarkMode && styles.textLight]}>
        {item.description}
      </Text>
      <View style={styles.groupActions}>
        <TouchableOpacity style={styles.actionButton}>
          <Ionicons name="chatbubble-outline" size={20} color={isDarkMode ? "#fff" : "#333"} />
          <Text style={[styles.actionText, isDarkMode && styles.textLight]}>讨论</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton}>
          <Ionicons name="calendar-outline" size={20} color={isDarkMode ? "#fff" : "#333"} />
          <Text style={[styles.actionText, isDarkMode && styles.textLight]}>活动</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton}>
          <Ionicons name="people-outline" size={20} color={isDarkMode ? "#fff" : "#333"} />
          <Text style={[styles.actionText, isDarkMode && styles.textLight]}>成员</Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  // 渲染推荐小组项
  const renderRecommendedGroupItem = ({ item }) => (
    <TouchableOpacity 
      style={[
        styles.groupCard, 
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}
      onPress={() => navigation.navigate('GroupDetail', { groupId: item.id })}
    >
      <View style={styles.groupHeader}>
        <Image source={{ uri: item.image }} style={styles.groupImage} />
        <View style={styles.groupInfo}>
          <Text style={[styles.groupName, isDarkMode && styles.textLight]}>
            {item.name}
          </Text>
          <Text style={[styles.groupMeta, isDarkMode && styles.textLight]}>
            {item.members} 成员
          </Text>
        </View>
      </View>
      <Text style={[styles.groupDescription, isDarkMode && styles.textLight]}>
        {item.description}
      </Text>
      <View style={styles.tagsContainer}>
        {item.tags.map((tag, index) => (
          <View key={index} style={styles.tag}>
            <Text style={styles.tagText}>{tag}</Text>
          </View>
        ))}
      </View>
      <TouchableOpacity 
        style={[
          styles.joinButton,
          isDarkMode ? styles.joinButtonDark : styles.joinButtonLight
        ]}
      >
        <Text style={styles.joinButtonText}>加入小组</Text>
      </TouchableOpacity>
    </TouchableOpacity>
  );

  // 渲染今日讨论项
  const renderDiscussionItem = ({ item }) => (
    <TouchableOpacity 
      style={[
        styles.discussionCard, 
        isDarkMode ? styles.cardDark : styles.cardLight
      ]}
      onPress={() => navigation.navigate('DiscussionDetail', { 
        discussionId: item.id,
        groupId: item.groupId 
      })}
    >
      <View style={styles.discussionGroupInfo}>
        <Text style={[styles.groupLabel, isDarkMode && styles.textLight]}>
          {item.groupName}
        </Text>
      </View>
      <Text style={[styles.discussionTitle, isDarkMode && styles.textLight]}>
        {item.title}
      </Text>
      <View style={styles.discussionMeta}>
        <Text style={[styles.discussionMetaText, isDarkMode && styles.textLight]}>
          {item.replies} 回复 · 最近回复: {item.lastReplyTime}
        </Text>
        <TouchableOpacity style={styles.replyButton}>
          <Ionicons name="chatbubble-outline" size={18} color={isDarkMode ? "#ddd" : "#666"} />
          <Text style={[styles.replyText, isDarkMode && styles.textLight]}>
            回复
          </Text>
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[
      styles.container,
      isDarkMode ? styles.containerDark : styles.containerLight
    ]}>
      {/* 页面标题 */}
      <View style={styles.header}>
        <Text style={[styles.screenTitle, isDarkMode && styles.textLight]}>
          互助小组
        </Text>
        <Text style={[styles.screenSubtitle, isDarkMode && styles.textLight]}>
          与志同道合的朋友一起坚持健康生活
        </Text>
      </View>

      {/* 搜索框 */}
      <View style={[
        styles.searchContainer,
        isDarkMode ? styles.searchDark : styles.searchLight
      ]}>
        <Ionicons
          name="search-outline"
          size={20}
          color={isDarkMode ? "#999" : "#666"}
          style={styles.searchIcon}
        />
        <TextInput
          style={[styles.searchInput, isDarkMode && styles.textLight]}
          placeholder="搜索小组..."
          placeholderTextColor={isDarkMode ? "#999" : "#999"}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>
      
      {/* 标签栏 */}
      <View style={styles.tabBar}>
        {['我的小组', '推荐小组', '今日讨论'].map(tab => (
          <TouchableOpacity
            key={tab}
            style={[
              styles.tab,
              activeTab === tab && styles.activeTab,
              activeTab === tab && isDarkMode && styles.activeTabDark
            ]}
            onPress={() => setActiveTab(tab)}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === tab && styles.activeTabText,
                isDarkMode && styles.textLight,
                activeTab === tab && isDarkMode && styles.activeTabTextDark
              ]}
            >
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* 内容区域 */}
      <ScrollView style={styles.content}>
        {activeTab === '我的小组' && (
          <View>
            <FlatList
              data={myGroups}
              renderItem={renderMyGroupItem}
              keyExtractor={item => item.id}
              scrollEnabled={false}
            />
            <TouchableOpacity 
              style={[
                styles.createGroupButton,
                isDarkMode ? styles.createGroupButtonDark : styles.createGroupButtonLight
              ]}
            >
              <Ionicons name="add-circle-outline" size={22} color="#fff" />
              <Text style={styles.createGroupText}>创建新小组</Text>
            </TouchableOpacity>
          </View>
        )}

        {activeTab === '推荐小组' && (
          <FlatList
            data={recommendedGroups}
            renderItem={renderRecommendedGroupItem}
            keyExtractor={item => item.id}
            scrollEnabled={false}
          />
        )}

        {activeTab === '今日讨论' && (
          <FlatList
            data={todayDiscussions}
            renderItem={renderDiscussionItem}
            keyExtractor={item => item.id}
            scrollEnabled={false}
          />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  containerLight: {
    backgroundColor: '#f5f5f5',
  },
  containerDark: {
    backgroundColor: '#121212',
  },
  textLight: {
    color: '#f5f5f5',
  },
  header: {
    marginBottom: 16,
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  screenSubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 10,
    marginBottom: 16,
  },
  searchLight: {
    backgroundColor: '#fff',
  },
  searchDark: {
    backgroundColor: '#2a2a2a',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
  },
  tabBar: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#4CAF50',
  },
  activeTabDark: {
    borderBottomColor: '#81C784',
  },
  tabText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#777',
  },
  activeTabText: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  activeTabTextDark: {
    color: '#81C784',
  },
  content: {
    flex: 1,
  },
  groupCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  cardLight: {
    backgroundColor: '#fff',
  },
  cardDark: {
    backgroundColor: '#2a2a2a',
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  groupImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  groupMeta: {
    fontSize: 14,
    color: '#777',
  },
  badgeContainer: {
    backgroundColor: '#FF5722',
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  groupDescription: {
    fontSize: 15,
    color: '#555',
    marginBottom: 16,
    lineHeight: 20,
  },
  groupActions: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionText: {
    marginLeft: 6,
    fontSize: 14,
    color: '#555',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  tag: {
    backgroundColor: '#E8F5E9',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    color: '#2E7D32',
    fontSize: 12,
    fontWeight: '500',
  },
  joinButton: {
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  joinButtonLight: {
    backgroundColor: '#4CAF50',
  },
  joinButtonDark: {
    backgroundColor: '#388E3C',
  },
  joinButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 15,
  },
  discussionCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  discussionGroupInfo: {
    marginBottom: 8,
  },
  groupLabel: {
    fontSize: 14,
    color: '#4CAF50',
    fontWeight: '500',
  },
  discussionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
    lineHeight: 22,
  },
  discussionMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  discussionMetaText: {
    fontSize: 13,
    color: '#777',
  },
  replyButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  replyText: {
    fontSize: 13,
    color: '#666',
    marginLeft: 4,
  },
  createGroupButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 10,
    marginTop: 8,
    marginBottom: 24,
  },
  createGroupButtonLight: {
    backgroundColor: '#4CAF50',
  },
  createGroupButtonDark: {
    backgroundColor: '#388E3C',
  },
  createGroupText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
});

export default SupportGroupScreen; 